/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useCurrencyService } from "../hooks/useAdminServices";
import { useHotel } from "./HotelContext";
import {
  CurrencySettingsDto,
  CurrencyFormatInfo,
} from "../services/admin/types/currency.types";
import { defaultCurrencyFormats } from "../utils/currency";
import { logger } from "../utils/logger";
import { API_BASE_URL } from "../services/common/constants";

interface AvailableCurrenciesResponse {
  baseCurrency: string;
  supportedCurrencies: string[];
  allowSelection: boolean;
  exchangeRates?: Record<string, number>;
  currencyFormats?: Record<string, CurrencyFormatInfo>;
}

interface CurrencyState {
  baseCurrency: string;
  displayCurrency: string;
  exchangeRates: Record<string, number>;
  formats: Record<string, CurrencyFormatInfo>;
  allowGuestSelection: boolean;
  supportedCurrencies: string[];
  isLoading: boolean;
  error: string | null;
}

interface CurrencyContextValue extends CurrencyState {
  setDisplayCurrency: (currency: string) => void;
  refreshSettings: () => Promise<void>;
  convertAmount: (
    amount: number,
    fromCurrency: string,
    toCurrency: string,
  ) => number;
  formatAmount: (amount: number, currency: string, locale?: string) => string;
}

const defaultState: CurrencyState = {
  baseCurrency: "PLN",
  displayCurrency: "PLN",
  exchangeRates: { PLN: 1.0 },
  formats: {},
  allowGuestSelection: true,
  supportedCurrencies: ["PLN"],
  isLoading: false,
  error: null,
};

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error("useCurrency must be used within a CurrencyProvider");
  }
  return context;
};

interface CurrencyProviderProps {
  children: ReactNode;
  isGuest?: boolean;
  initialSettings?: CurrencySettingsDto;
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({
  children,
  isGuest = false,
  initialSettings,
}) => {
  const currencyService = useCurrencyService();
  const { myHotelInfo } = useHotel();

  // Check if user has a hotel (only relevant for admin, not guest)
  const hasHotel = isGuest || !!myHotelInfo?.hotelId;
  const { token: routeToken } = useParams<{ token?: string }>();

  // When the provider is rendered outside a matched Route (e.g., top-level in GuestAppV2),
  // useParams will return undefined. Parse the token from the current pathname as a fallback.
  const token =
    routeToken ||
    (() => {
      const path = window.location.pathname;
      const match =
        path.match(/\/g2\/([^/]+)/) || // guest v2
        path.match(/\/g\/([^/]+)/); // guest v1
      return match?.[1];
    })();
  const [state, setState] = useState<CurrencyState>(defaultState);

  const loadSettings = useCallback(async () => {
    // If bootstrap provided initial settings for admin, use them directly
    if (!isGuest && initialSettings) {
      setState({
        baseCurrency: initialSettings.baseCurrency,
        displayCurrency: initialSettings.baseCurrency,
        exchangeRates: initialSettings.exchangeRates,
        formats: initialSettings.currencyFormats,
        allowGuestSelection: initialSettings.allowGuestCurrencySelection,
        supportedCurrencies: initialSettings.supportedCurrencies,
        isLoading: false,
        error: null,
      });
      localStorage.setItem("currencySettings", JSON.stringify(initialSettings));
      return;
    }
    // For admin: require currencyService AND hasHotel to avoid 403 errors
    // For guest: the original logic handles missing token gracefully
    if (!isGuest && (!currencyService || !hasHotel)) return;
    if (isGuest && !token) {
      // For guest without token, use cached settings or defaults (handled below)
      // Just set loading to false and return
      setState((prev) => ({ ...prev, isLoading: false }));
      return;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      if (isGuest) {
        // For guest pages, fetch available currencies from API
        if (token) {
          try {
            // Fetch available currencies from the new currency endpoint
            const response = await axios.get<AvailableCurrenciesResponse>(
              `${API_BASE_URL}/g/currency/${token}`,
            );
            const availableCurrencies = response.data;

            // Get saved display currency preference from localStorage
            const savedCurrency = localStorage.getItem("guestDisplayCurrency");
            const preferredCurrency =
              savedCurrency || availableCurrencies.baseCurrency;

            // Build formats - use API formats if available, otherwise use defaults
            const formats: Record<string, CurrencyFormatInfo> = {};
            if (availableCurrencies.currencyFormats) {
              // Convert API formats to CurrencyFormatInfo type
              Object.entries(availableCurrencies.currencyFormats).forEach(
                ([currency, format]) => {
                  // API may return PascalCase (C#) or camelCase keys
                  const f = format as unknown as Record<string, unknown>;
                  formats[currency] = {
                    symbol: (f.Symbol as string) || format.symbol || currency,
                    position: ((f.Position as string) ||
                      format.position ||
                      "prefix") as "prefix" | "suffix",
                    decimals: (f.Decimals as number) || format.decimals || 2,
                    thousandsSeparator:
                      (f.ThousandsSeparator as string) ||
                      format.thousandsSeparator ||
                      ",",
                    decimalSeparator:
                      (f.DecimalSeparator as string) || format.decimalSeparator || ".",
                  };
                },
              );
            }
            // Add default formats for any missing currencies
            availableCurrencies.supportedCurrencies.forEach((currency) => {
              if (!formats[currency] && defaultCurrencyFormats[currency]) {
                formats[currency] = defaultCurrencyFormats[currency];
              }
            });

            // Use exchange rates from API if available, otherwise default to 1:1
            const exchangeRates: Record<string, number> =
              availableCurrencies.exchangeRates || {};
            // Ensure all supported currencies have an exchange rate
            availableCurrencies.supportedCurrencies.forEach((currency) => {
              if (!exchangeRates[currency]) {
                exchangeRates[currency] = 1.0;
              }
            });

            setState((prev) => ({
              ...prev,
              baseCurrency: availableCurrencies.baseCurrency,
              displayCurrency: preferredCurrency,
              exchangeRates: exchangeRates,
              formats: formats,
              allowGuestSelection: availableCurrencies.allowSelection,
              supportedCurrencies: availableCurrencies.supportedCurrencies,
              isLoading: false,
            }));

            // Cache settings for offline use
            const settingsToCache: CurrencySettingsDto = {
              baseCurrency: availableCurrencies.baseCurrency,
              allowGuestCurrencySelection: availableCurrencies.allowSelection,
              supportedCurrencies: availableCurrencies.supportedCurrencies,
              exchangeRates: exchangeRates,
              currencyFormats: formats,
              lastUpdated: new Date().toISOString(),
              autoUpdateRates: false,
              exchangeRateProvider: undefined,
            };
            localStorage.setItem(
              "currencySettings",
              JSON.stringify(settingsToCache),
            );
          } catch (error) {
            logger.error("Failed to load available currencies:", error);

            // Fall back to cached settings if available
            const savedSettings = localStorage.getItem("currencySettings");
            if (savedSettings) {
              const settings = JSON.parse(savedSettings) as CurrencySettingsDto;
              const savedCurrency = localStorage.getItem(
                "guestDisplayCurrency",
              );
              const preferredCurrency = savedCurrency || settings.baseCurrency;

              setState((prev) => ({
                ...prev,
                baseCurrency: settings.baseCurrency,
                displayCurrency: preferredCurrency,
                exchangeRates: settings.exchangeRates,
                formats: settings.currencyFormats,
                allowGuestSelection: settings.allowGuestCurrencySelection,
                supportedCurrencies: settings.supportedCurrencies,
                isLoading: false,
              }));
            } else {
              // Use defaults if no cached settings
              setState((prev) => ({ ...prev, isLoading: false }));
            }
          }
        } else {
          // No token available, use cached settings or defaults
          const savedSettings = localStorage.getItem("currencySettings");
          if (savedSettings) {
            const settings = JSON.parse(savedSettings) as CurrencySettingsDto;
            const savedCurrency = localStorage.getItem("guestDisplayCurrency");
            const preferredCurrency = savedCurrency || settings.baseCurrency;

            setState((prev) => ({
              ...prev,
              baseCurrency: settings.baseCurrency,
              displayCurrency: preferredCurrency,
              exchangeRates: settings.exchangeRates,
              formats: settings.currencyFormats,
              allowGuestSelection: settings.allowGuestCurrencySelection,
              supportedCurrencies: settings.supportedCurrencies,
              isLoading: false,
            }));
          } else {
            setState((prev) => ({ ...prev, isLoading: false }));
          }
        }
      } else if (currencyService) {
        // For admin pages, load from API
        const result = await currencyService.getSettings();

        if (result.success) {
          const settings = result.data;

          setState({
            baseCurrency: settings.baseCurrency,
            displayCurrency: settings.baseCurrency,
            exchangeRates: settings.exchangeRates,
            formats: settings.currencyFormats,
            allowGuestSelection: settings.allowGuestCurrencySelection,
            supportedCurrencies: settings.supportedCurrencies,
            isLoading: false,
            error: null,
          });

          // Cache settings for guest pages
          localStorage.setItem("currencySettings", JSON.stringify(settings));
        } else {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            error: result.error?.message || "Failed to load currency settings",
          }));
        }
      }
    } catch (error) {
      logger.error("Failed to load currency settings:", error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: "Failed to load currency settings",
      }));
    }
  }, [currencyService, isGuest, token, hasHotel, initialSettings]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const setDisplayCurrency = useCallback(
    (currency: string) => {
      if (state.supportedCurrencies.includes(currency)) {
        setState((prev) => ({ ...prev, displayCurrency: currency }));
        if (isGuest) {
          // Save to localStorage only (no server-side persistence for MVP)
          localStorage.setItem("guestDisplayCurrency", currency);
        }
      }
    },
    [state.supportedCurrencies, isGuest],
  );

  const convertAmount = useCallback(
    (amount: number, fromCurrency: string, toCurrency: string): number => {
      if (fromCurrency === toCurrency) return amount;

      const fromRate = state.exchangeRates[fromCurrency] || 1;
      const toRate = state.exchangeRates[toCurrency] || 1;

      return (amount / fromRate) * toRate;
    },
    [state.exchangeRates],
  );

  const formatAmount = useCallback(
    (amount: number, currency: string, locale?: string): string => {
      const format =
        state.formats[currency] || defaultCurrencyFormats[currency];

      if (!format && locale) {
        try {
          return new Intl.NumberFormat(locale, {
            style: "currency",
            currency: currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(amount);
        } catch (error) {
          logger.error("Failed to format currency:", error);
        }
      }

      if (format) {
        const parts = amount.toFixed(format.decimals).split(".");
        const integerPart = parts[0];
        const decimalPart = parts[1];

        const integerWithSeparators = integerPart.replace(
          /\B(?=(\d{3})+(?!\d))/g,
          format.thousandsSeparator,
        );

        const formattedNumber = decimalPart
          ? `${integerWithSeparators}${format.decimalSeparator}${decimalPart}`
          : integerWithSeparators;

        if (format.position === "prefix") {
          return `${format.symbol}${format.symbol.match(/[A-Z]/) ? " " : ""}${formattedNumber}`;
        } else {
          return `${formattedNumber} ${format.symbol}`;
        }
      }

      // Fallback formatting
      return `${currency} ${amount.toFixed(2)}`;
    },
    [state.formats],
  );

  const contextValue: CurrencyContextValue = {
    ...state,
    setDisplayCurrency,
    refreshSettings: loadSettings,
    convertAmount,
    formatAmount,
  };

  return (
    <CurrencyContext.Provider value={contextValue}>
      {children}
    </CurrencyContext.Provider>
  );
};
